import { useState } from "react"
import { useParams, Link } from "wouter"
import { useQuery } from "@tanstack/react-query"
import { Calendar, ChevronRight } from "lucide-react"
import { useTranslation } from "react-i18next"
import { api, ApiRequestError } from "@/lib/api"
import { FindMyBookingDialog } from "@/components/FindMyBookingDialog"
import type { GuestReservationList } from "@fohet/caravan-booker-shared/types"

const statusColors: Record<string, string> = {
    created: "bg-orange-50 border-orange-200 text-orange-700",
    paid: "bg-green-50 border-green-200 text-green-700",
    confirmed: "bg-blue-50 border-blue-200 text-blue-700",
    active: "bg-brand/10 border-brand/30 text-green-800",
}

export const GuestPage = () => {
    const { t } = useTranslation()
    const { token } = useParams<{ token: string }>()
    const [showDialog, setShowDialog] = useState(false)

    const { data: reservations, isLoading, error } = useQuery({
        queryKey: ["guest-reservations", token],
        queryFn: () => api.get<GuestReservationList>(`/guest/${token}`),
        retry: false,
    })

    if (isLoading) {
        return (
            <div className="flex items-center justify-center py-24">
                <div className="text-slate-400 font-bold">{t("common.loading")}</div>
            </div>
        )
    }

    if (error) {
        const isExpired = error instanceof ApiRequestError && error.code === "GUEST_RECOVERY_EXPIRED"
        return (
            <div className="max-w-md mx-auto space-y-4 py-8">
                <div className="bg-red-50 border border-red-200 rounded-2xl p-6">
                    <p className="text-red-700 font-bold">
                        {isExpired ? t("errors.GUEST_RECOVERY_EXPIRED") : t("guest.recover.errorGeneric")}
                    </p>
                </div>
                <button
                    onClick={() => setShowDialog(true)}
                    className="w-full bg-brand text-white font-black rounded-[28px] py-3 px-6 shadow-xl hover:bg-brand-dark active:scale-95 transition-all"
                >
                    {t("guest.recover.requestNew")}
                </button>
                <Link to="/" className="block text-center text-sm font-bold text-slate-500 hover:text-slate-700 transition-colors">
                    {t("guest.recover.backToHome")}
                </Link>
                <FindMyBookingDialog open={showDialog} onClose={() => setShowDialog(false)} />
            </div>
        )
    }

    if (!reservations || reservations.length === 0) {
        return (
            <div className="max-w-md mx-auto space-y-4 py-8">
                <div className="bg-slate-50 border border-slate-200 rounded-2xl p-6 text-center">
                    <p className="text-slate-600 font-bold">{t("guest.recover.noReservations")}</p>
                </div>
                <Link to="/" className="block text-center text-sm font-bold text-slate-500 hover:text-slate-700 transition-colors">
                    {t("guest.recover.backToHome")}
                </Link>
            </div>
        )
    }

    return (
        <div className="max-w-lg mx-auto space-y-5 py-4">
            <div>
                <h1 className="text-3xl font-black text-slate-900 mb-1">{t("guest.recover.pageTitle")}</h1>
                <p className="text-slate-500 font-bold">{t("guest.recover.pageSubtitle")}</p>
            </div>

            <div className="space-y-3">
                {reservations.map((r) => (
                    <Link key={r.magicToken} to={`/r/${r.magicToken}`}>
                        <div className="bg-white rounded-[30px] border border-slate-100 shadow-xl p-5 hover:shadow-2xl hover:-translate-y-0.5 transition-all duration-200 cursor-pointer active:scale-95 flex items-center justify-between gap-4">
                            <div className="flex items-start gap-3 min-w-0">
                                <div className="bg-slate-100 rounded-2xl p-2.5 shrink-0">
                                    <Calendar className="size-4 text-slate-500" />
                                </div>
                                <div className="min-w-0">
                                    <p className="font-black text-slate-900 truncate">{r.arealName}</p>
                                    <p className="text-sm font-bold text-slate-500">
                                        {r.arrivalDate} &ndash; {r.departureDate}
                                    </p>
                                    <span className={`inline-block mt-1.5 text-xs font-black uppercase tracking-widest px-2.5 py-1 rounded-lg border ${statusColors[r.status] ?? "bg-slate-100 border-slate-200 text-slate-600"}`}>
                                        {t(`common.status.${r.status}`, { defaultValue: r.status })}
                                    </span>
                                </div>
                            </div>
                            <ChevronRight className="size-5 text-slate-400 shrink-0" />
                        </div>
                    </Link>
                ))}
            </div>

            <Link to="/" className="block text-center text-sm font-bold text-slate-500 hover:text-slate-700 transition-colors pt-2">
                {t("guest.recover.backToHome")}
            </Link>
        </div>
    )
}
